import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib';
import { AccessLevel, hasAccess, SectionKey } from '@/types';
import { ADMIN_USER_QUERY_KEYS } from '@/constants';

interface UserPermissions {
  accessLevel: AccessLevel | null;
  hasAccessTo: (section: SectionKey) => boolean;
  isSuperAdmin: boolean;
  isLoading: boolean;
}

export function usePermissions(): UserPermissions {
  const { data: currentUserAccess, isLoading } = useQuery({
    queryKey: [ADMIN_USER_QUERY_KEYS.CURRENT_PERMISSIONS],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        return null;
      }

      const { data, error } = await supabase
        .from('admin_users')
        .select('access_level')
        .eq('id', session.user.id)
        .single();

      if (error || !data) {
        return null;
      }

      return data.access_level as AccessLevel;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    accessLevel: currentUserAccess || null,
    hasAccessTo: (section: SectionKey) => {
      if (!currentUserAccess) return false;
      return hasAccess(currentUserAccess, section);
    },
    isSuperAdmin: currentUserAccess === 'super_admin',
    isLoading
  };
} 